import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, History, ChevronRight, ChevronLeft, Trash2, RotateCcw, Info, Printer } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PILLARS = [
  {
    key: 'breathing',
    name: 'Breathing & Being',
    short: 'Breathing',
    category: 'Spiritual',
    color: '#8BADA4',
    question: 'How present and connected to your breath have you been today?',
    prompts: [
      'Have I taken any conscious breaths today — even three slow ones? (or has my breath stayed shallow and unnoticed?)',
      'Did I pause between tasks, or rush from one thing straight into the next?',
      'Can I feel my breath drop low into my belly, or is it stuck high and tight in my chest?',
      'Did I give myself any real stillness today — a moment of just being, not doing?',
      'When stress rose, did I breathe through it, or catch myself holding my breath?',
    ],
    guidance: {
      low: [
        'Your nervous system is asking for support. Start with just 3 slow breaths right now.',
        'Try a 2-minute pause: close your eyes and simply notice your breath without changing it.',
        'Before your next transition (meal, task, outing), take one conscious breath.',
      ],
      mid: [
        'You have some awareness here. Try adding a breathing pause between activities today.',
        'Explore a 5-minute breath awareness practice — no technique needed, just notice.',
        'Slow your transitions. Walk a little slower between rooms. Let your breath guide the pace.',
      ],
      high: [
        'Beautiful rhythm here. Your nervous system feels safe. Keep honoring those pauses.',
        'Consider deepening your practice — try a new breathwork style or extend your pause time.',
      ],
    },
  },
  {
    key: 'nourishment',
    name: 'Nourishment & Timing',
    short: 'Nourishment',
    category: 'Physical',
    color: '#C88D74',
    question: 'How nourished and well-timed has your eating been today?',
    prompts: [
      'Have I eaten at regular, evenly-spaced intervals — or skipped and grazed?',
      'Did my meals include fat, fiber and protein to steady my blood sugar?',
      'Am I eating real, whole food today, or mostly processed on-the-go fuel?',
      'Did I eat because I was truly hungry, or out of stress, boredom or habit?',
      'How did I feel 30 minutes after eating — steady and satisfied, or crashed and craving?',
    ],
    guidance: {
      low: [
        'Your blood sugar and body rhythm need stabilizing. Focus on fat, fiber, and protein at your next meal.',
        'Eat within the next hour if you haven\'t. Consistent intervals calm your nervous system — think of your body like a toddler that thrives with structure.',
        'Keep it simple: an egg, avocado, handful of nuts. Don\'t overcomplicate it.',
      ],
      mid: [
        'Good foundation. See if you can add more fat or fiber to your next meal for a smoother energy ride.',
        'Check your timing — are your meals roughly evenly spaced? Rhythm matters as much as content.',
        'Try noticing how you feel 30 minutes after eating. This builds food awareness over time.',
      ],
      high: [
        'Your body is well-fueled and in rhythm. Trust what\'s working.',
        'This is a great day to experiment — try a new whole food or adjust a meal timing slightly.',
      ],
    },
  },
  {
    key: 'thoughts',
    name: 'Thoughts & Inner Dialogue',
    short: 'Thoughts',
    category: 'Mental',
    color: '#9B8EC4',
    question: 'How kind and aware has your inner dialogue been today?',
    prompts: [
      'Would I speak to a friend the way I\u2019ve spoken to myself today?',
      'When a harsh thought showed up, did I believe it — or get curious about it?',
      'Have I caught myself replaying worry or \u201cwhat-ifs\u201d on a loop?',
      'Did I create any quiet today, or has my mind been running non-stop?',
      'What one thought has taken up the most space today — and is it even true?',
    ],
    guidance: {
      low: [
        'Your inner critic may be loud today. That\'s okay — awareness is the first step.',
        'Try this: when you catch a harsh thought, simply say "interesting" and move on. No judgment.',
        'Curiosity is like instant electroshock therapy for the inner critic — ask "why do I think that?" instead of believing it.',
      ],
      mid: [
        'You\'re noticing your patterns. Try reframing one recurring thought today — not with positivity, but with curiosity.',
        'Remember: every thought influences every cell. You have 120 trillion new cells this year listening to you.',
        'Write down one thought that felt heavy today. Just seeing it on paper often loosens its grip.',
      ],
      high: [
        'Your mindset is clear and kind today. This is the frequency your cells are building from.',
        'Use this clarity to set an intention for the week. When the mind is quiet, intention is powerful.',
      ],
    },
  },
  {
    key: 'hydration',
    name: 'Hydration & Flow',
    short: 'Hydration',
    category: 'Physical',
    color: '#6BA3BE',
    question: 'How well-hydrated and mineral-supported have you been today?',
    prompts: [
      'Have I had enough water today? (aiming for roughly 1.5\u20132L)',
      'Did I add any enhancements? (a pinch of quality salt, coconut water, minerals, silica, sea moss, clean electrolytes)',
      'Is my water clean and quality, or straight from the tap?',
      'Did I front-load my hydration earlier in the day, or leave it all until night?',
      'What is my body telling me — clear urine and steady energy, or headache, fatigue, dry lips?',
    ],
    guidance: {
      low: [
        'Everything feels harder when hydration is off — digestion, sleep, focus, movement.',
        'Add a pinch of quality salt (pink/sea) to your next glass of water for better absorption.',
        'Consider: sea moss, silica, or mineral drops. Our food simply doesn\'t carry the minerals it used to.',
      ],
      mid: [
        'You\'re getting some fluids in. Try front-loading your hydration — drink more in the first half of the day.',
        'Add electrolytes or a binder like chlorella or food-grade diatomaceous earth to support detox flow.',
        'Notice: are you drinking because you\'re thirsty, or proactively? Shift toward proactive.',
      ],
      high: [
        'Great flow today. Your body is well-supported on a cellular level.',
        'Maintain this rhythm — it\'s one of the fastest ways to support every other pillar.',
      ],
    },
  },
  {
    key: 'movement',
    name: 'Movement & Body',
    short: 'Movement',
    category: 'Physical',
    color: '#7CAE7A',
    question: 'How connected to your body through movement have you been today?',
    prompts: [
      'Have I moved my body at all today — a walk, a stretch, some fascial work?',
      'Did I move to inhabit and free my body, or only to burn calories / punish it?',
      'If I\u2019m in pain, did I honour it with gentle, safe movement rather than pushing through?',
      'Have I sat still for long stretches without breaking it up?',
      'Does my body feel more open and capable after today, or stiffer and more shut down?',
    ],
    guidance: {
      low: [
        'Pain is information, not an enemy. If movement feels hard, start with stability and safety.',
        'Try 5 minutes of gentle movement — a walk, a stretch, fascial work. Respectful movement brings freedom.',
        'Priority today: reduce pain first, then support your endocrine system. Don\'t push.',
      ],
      mid: [
        'Your body is asking for more connection. Try adding 10 minutes of mindful movement today.',
        'Focus on functional patterns — movements that make daily life feel easier and more efficient.',
        'Consider: are you moving to burn calories, or to inhabit your body? The shift matters.',
      ],
      high: [
        'You\'re in a strong movement rhythm. Your body feels safe and expressive.',
        'This might be a good day to build strength or try something new — your foundation is solid.',
      ],
    },
  },
  {
    key: 'sleep',
    name: 'Sleep & Rest',
    short: 'Sleep',
    category: 'Emotional',
    color: '#5B7B9A',
    question: 'How rested and regulated does your nervous system feel today?',
    prompts: [
      'Did I get enough sleep to feel genuinely restored — not just \u201cget through\u201d?',
      'Did I wind down before bed (dim lights, screens off), or crash mid-scroll?',
      'Did I allow any intentional rest in my day, or push non-stop?',
      'Do I wake feeling repaired, or still wired and tired?',
      'Is my nervous system getting the message that it\u2019s safe to fully switch off?',
    ],
    guidance: {
      low: [
        'Sleep isn\'t just rest — it\'s repair, integration, and regulation. Your body needs safety signals.',
        'Tonight: no screens 30 minutes before bed. Dim the lights. Let your body know it\'s safe to rest.',
        'Rest is productive. If sleep was poor, allow intentional downtime today — even 10 minutes eyes-closed.',
      ],
      mid: [
        'Some rest came through but there\'s room to deepen. Review your wind-down routine.',
        'Consider: is your bedroom dark, cool, and quiet? Small environmental changes make big differences.',
        'Add a nervous system regulation practice before bed — slow breathing, gentle stretching, journaling.',
      ],
      high: [
        'Deep rest achieved. Your nervous system is recovering well. This supports everything else.',
        'Keep honoring your sleep routine — consistency is the foundation of lasting regulation.',
      ],
    },
  },
  {
    key: 'emotions',
    name: 'Emotional Awareness',
    short: 'Emotions',
    category: 'Emotional',
    color: '#D4A27F',
    question: 'How aware and accepting of your emotions have you been today?',
    prompts: [
      'Can I name what I\u2019m actually feeling today, or is it a vague \u201cfine\u201d?',
      'Did I let my emotions move and express — or push them down?',
      'Where am I feeling this in my body? (tight chest, clenched jaw, heavy gut?)',
      'Have I been especially hard on myself around shame, fear or guilt today?',
      'Did I make space to simply feel, without needing to fix or explain it?',
    ],
    guidance: {
      low: [
        'Emotions are signals, not problems. If you\'re feeling stuck, that\'s energy looking for movement.',
        'Expression creates movement. Suppression creates stagnation. Try writing, speaking, or moving what you feel.',
        'Be especially gentle with shame and fear today — they hold enormous power when unrecognized.',
      ],
      mid: [
        'You\'re aware of what\'s moving through you. Try naming the emotion without judging it.',
        'Create space for feelings without trying to fix them. Sometimes just witnessing is enough.',
        'Notice where emotions live in your body. Tightness, heat, heaviness — these are your body\'s language.',
      ],
      high: [
        'You\'re in tune with your emotional landscape. Energy is moving freely through you.',
        'This emotional clarity is a gift. Use it to reflect, connect, or set boundaries with compassion.',
      ],
    },
  },
  {
    key: 'wholeSelf',
    name: 'Whole Self Integration',
    short: 'Whole Self',
    category: 'Spiritual',
    color: '#A89080',
    question: 'How aligned do your inner guidance and outer actions feel today?',
    prompts: [
      'Do my actions today match what I actually value, or am I just going through the motions?',
      'Did I listen to my gut / intuition, or override it with \u201cshould\u201d?',
      'Where am I forcing effort when surrender might be the real work?',
      'Have I done anything today that feeds meaning — nature, creativity, reflection, prayer?',
      'Do I feel like myself today, or a bit fragmented and scattered?',
    ],
    guidance: {
      low: [
        'When life feels fractured, it\'s often the ego-soul connection that needs attention.',
        'Pause and ask: "What does my inner compass say about today?" Listen before acting.',
        'Alignment isn\'t about perfection — it\'s about coherence. Small honest steps count.',
      ],
      mid: [
        'There\'s some alignment here. Try one action today that honors your intuition over your logic.',
        'Reflect: where are you choosing effort over surrender? Sometimes letting go IS the work.',
        'Practices that foster meaning — journaling, nature, creative expression — feed this pillar.',
      ],
      high: [
        'Inner guidance and outer life are in harmony. This is where life feels intentional, not accidental.',
        'Trust this alignment. It\'s the result of the inner work you\'ve been doing.',
      ],
    },
  },
  {
    key: 'selfInContext',
    name: 'The Self in Context',
    short: 'Self in Context',
    category: 'Integration',
    color: '#B8A090',
    question: 'How whole, resilient, and fully expressed do you feel today?',
    prompts: [
      'Taking everything together, how whole and \u201clike me\u201d do I feel right now?',
      'Which single pillar, if I supported it, would lift the most others today?',
      'Am I being honest and compassionate about the season I\u2019m in? (grief, caregiving, transition all shift capacity)',
      'Did I set one clear intention today and let things arrange around it?',
      'Am I running on reserves and resilience, or genuinely replenished?',
    ],
    guidance: {
      low: [
        'This pillar reflects the whole. When it\'s low, it means other areas are asking for attention first.',
        'Be compassionate with where you are. Life circumstances — caregiving, grief, transitions — shift your capacity.',
        'Intention is 9/10th of the law. Set one small intention today and let everything else arrange around it.',
      ],
      mid: [
        'You\'re building. The stacking of the other pillars is starting to create a fuller expression of you.',
        'Notice which pillar, when supported, makes the biggest difference to how "whole" you feel.',
        'Remember: when we enter a phase where we can\'t address all areas, we\'re resilient — to a point. Tend to what you can.',
      ],
      high: [
        'You\'re in a beautiful rhythm of full expression. This is the culmination of tending to all pillars.',
        'This is vitality. This is what changes state. Savor it and notice what made today different.',
      ],
    },
  },
];

const getScoreLevel = (score) => {
  if (score <= 3) return 'low';
  if (score <= 6) return 'mid';
  return 'high';
};

const getOverallMessage = (score) => {
  if (score <= 3) return { title: 'Deep Support Needed', message: 'Your system is calling for care today. Focus on just one or two pillars — the ones that feel most urgent. Small, gentle actions create safety.', color: '#C88D74' };
  if (score <= 5) return { title: 'Building Awareness', message: 'You\'re noticing where your system needs support. That awareness is powerful. Today, focus on the lowest-scoring areas — they\'re where the biggest shifts will come.', color: '#D4A27F' };
  if (score <= 7) return { title: 'Growing Rhythm', message: 'Good momentum. Your system is finding its balance. Keep tending to the pillars that are lower — when the strained areas are supported, everything regulates.', color: '#7CAE7A' };
  return { title: 'Strong Vitality', message: 'You\'re in a beautiful rhythm. Your pillars are supporting each other. Maintain what\'s working and notice what made today feel this way.', color: '#5B7B9A' };
};

const escHtml = (s = '') => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_0a9ff351-8b5c-42db-9af2-82d74676456a/artifacts/zsl04d0x_Grounded%20Wellness%20%28Logo%29%20%281%29.png';

// Blank 9-spoke wheel as an SVG string (for printing / filling in by hand)
const buildWheelSVG = (size = 340, labelSize = 10, showNumbers = true) => {
  const cx = size / 2, cy = size / 2;
  const pad = size * 0.24;
  const R = size / 2 - pad;
  const n = PILLARS.length;
  const pt = (r, i) => {
    const a = (Math.PI * 2 * i / n) - Math.PI / 2;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  let svg = `<svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">`;
  for (let lvl = 1; lvl <= 5; lvl++) {
    const rr = R * lvl / 5;
    const poly = Array.from({ length: n }, (_, i) => pt(rr, i).map((v) => v.toFixed(1)).join(',')).join(' ');
    svg += `<polygon points="${poly}" fill="${lvl === 5 ? '#faf7f1' : 'none'}" stroke="#cfc7b6" stroke-width="0.8"/>`;
  }
  for (let i = 0; i < n; i++) {
    const [x, y] = pt(R, i);
    svg += `<line x1="${cx}" y1="${cy}" x2="${x.toFixed(1)}" y2="${y.toFixed(1)}" stroke="#cfc7b6" stroke-width="0.8"/>`;
    svg += `<circle cx="${cx}" cy="${cy}" r="1.6" fill="#8a6a5a"/>`;
    const [lx, ly] = pt(R + size * 0.07, i);
    const anchor = Math.abs(lx - cx) < 4 ? 'middle' : (lx < cx ? 'end' : 'start');
    svg += `<text x="${lx.toFixed(1)}" y="${(ly + labelSize / 3).toFixed(1)}" font-size="${labelSize}" fill="#3a4a3a" text-anchor="${anchor}" font-family="Georgia, serif">${escHtml(PILLARS[i].short)}</text>`;
  }
  if (showNumbers) {
    for (let lvl = 1; lvl <= 5; lvl++) {
      const rr = R * lvl / 5;
      svg += `<text x="${cx + 3}" y="${(cy - rr + 3).toFixed(1)}" font-size="${labelSize - 3}" fill="#b0a794" font-family="Georgia, serif">${lvl * 2}</text>`;
    }
  }
  svg += `</svg>`;
  return svg;
};

const printCss = `
  @media print { @page { margin: 1.4cm; } }
  body { font-family: Georgia, 'Times New Roman', serif; color:#2f2a24; line-height:1.55; max-width:760px; margin:24px auto; padding:0 20px; }
  .logo { display:block; height:78px; margin:0 auto 14px; }
  h1 { font-size:26px; color:#3a4a3a; margin:0 0 4px; }
  .sub { color:#8a6a5a; font-style:italic; margin:0 0 18px; }
  .intro { font-size:15px; color:#4a4a4a; margin-bottom:18px; }
  .wheelwrap { text-align:center; margin:14px 0 26px; }
  .pillar { margin:16px 0; break-inside:avoid; }
  .pillar h2 { font-size:16px; color:#3a4a3a; margin:0 0 4px; border-bottom:1px solid #e5ded2; padding-bottom:3px; }
  .pillar .cat { font-size:11px; color:#a89f8e; font-style:italic; font-weight:normal; }
  .q { font-style:italic; color:#5a4a3a; margin:6px 0; }
  ul { margin:6px 0 8px; padding-left:20px; }
  li { margin:3px 0; font-size:14px; }
  .scale { font-size:13px; color:#3a4a3a; margin-top:6px; }
  .pagebreak { page-break-before:always; }
  .grid { display:grid; grid-template-columns:1fr 1fr; gap:6px 10px; }
  .gcell { text-align:center; break-inside:avoid; }
  .foot { margin-top:24px; font-size:11px; color:#999; border-top:1px solid #e5ded2; padding-top:8px; }
`;

const printGuide = () => {
  const wheel = buildWheelSVG(360, 11, true);
  const pillHtml = PILLARS.map((p) => {
    let h = `<div class="pillar"><h2>${escHtml(p.name)} <span class="cat">${escHtml(p.category)}</span></h2>`;
    h += `<p class="q">"${escHtml(p.question)}"</p><ul>`;
    (p.prompts || []).forEach((pr) => { h += `<li>${escHtml(pr)}</li>`; });
    h += `</ul><p class="scale">My score today: ______ / 10</p></div>`;
    return h;
  }).join('');
  const sixWheels = Array.from({ length: 6 }, () => `<div class="gcell">${buildWheelSVG(250, 8, true)}</div>`).join('');
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"/><title>Wellness Wheel — Reflection Guide</title><style>${printCss}</style></head><body>
    <img class="logo" src="${LOGO_URL}" alt="Grounded Wellness with Jill"/>
    <h1>Wellness Wheel — Reflection Guide</h1>
    <p class="sub">Based on the 9 Pillars of Wellness</p>
    <p class="intro">Rate each area of your life today from 1 (needs deep support) to 10 (thriving). Let the questions guide you — there are no wrong answers. Focus your energy on the lowest pillars; when the most strained areas are supported, the whole system begins to regulate.</p>
    <div class="wheelwrap">${wheel}</div>
    ${pillHtml}
    <div class="pagebreak"></div>
    <h1>Blank Wheels</h1>
    <p class="sub">Print this page as many times as you like — one wheel per check-in.</p>
    <div class="grid">${sixWheels}</div>
    <p class="foot">Grounded Wellness with Jill — for reflection and education. Not a substitute for medical care.</p>
    </body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 500);
};

const printBlankWheels = () => {
  const sixWheels = Array.from({ length: 6 }, () => `<div class="gcell">${buildWheelSVG(250, 8, true)}</div>`).join('');
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"/><title>Wellness Wheel — Blank Wheels</title><style>${printCss}</style></head><body>
    <img class="logo" src="${LOGO_URL}" alt="Grounded Wellness with Jill"/>
    <h1>Wellness Wheel — Blank Wheels</h1>
    <p class="sub">6 wheels per page. Print as many pages as you like and track by hand.</p>
    <div class="grid">${sixWheels}</div>
    <p class="foot">Grounded Wellness with Jill — 9 Pillars of Wellness.</p>
    </body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 500);
};

const WellnessWheelPage = () => {
  const { user, hasAccess, loading } = useAuth();
  const [view, setView] = useState('assess'); // 'assess', 'results', 'history', 'trends'
  const [scores, setScores] = useState({});
  const [currentPillar, setCurrentPillar] = useState(0);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showInfo, setShowInfo] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];

  const fetchHistory = useCallback(async () => {
    if (!user) return;
    setLoadingHistory(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/wellness-wheel/history/${user.id}`);
      const data = await res.json();
      setHistory(data.entries || []);
    } catch (err) {
      console.error('Failed to load history', err);
    }
    setLoadingHistory(false);
  }, [user]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Check if there's already an entry for today
  useEffect(() => {
    const todayEntry = history.find(e => e.date === todayStr);
    if (todayEntry && Object.keys(scores).length === 0) {
      setScores(todayEntry.scores);
    }
  }, [history, todayStr, scores]);

  const handleScore = (pillarKey, value) => {
    setScores(prev => ({ ...prev, [pillarKey]: value }));
  };

  const allScored = Object.keys(scores).length === PILLARS.length;
  const overallScore = allScored
    ? (Object.values(scores).reduce((a, b) => a + b, 0) / PILLARS.length).toFixed(1)
    : null;

  const handleSave = async () => {
    if (!allScored) return;
    // Guests: show results immediately, no save
    if (!user) {
      setView('results');
      return;
    }
    setSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/wellness-wheel/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          scores,
          overall_score: parseFloat(overallScore),
          date: todayStr,
        }),
      });
      await fetchHistory();
      setView('results');
    } catch (err) {
      console.error('Failed to save', err);
    }
    setSaving(false);
  };

  const handleDelete = async (date) => {
    if (!user) return;
    try {
      await fetch(`${BACKEND_URL}/api/wellness-wheel/${user.id}/${date}`, { method: 'DELETE' });
      await fetchHistory();
      if (selectedEntry?.date === date) setSelectedEntry(null);
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  const handleReset = () => {
    setScores({});
    setCurrentPillar(0);
    setView('assess');
  };

  const radarData = PILLARS.map(p => ({
    pillar: p.short,
    score: scores[p.key] || 0,
    fullMark: 10,
  }));

  const selectedRadarData = selectedEntry
    ? PILLARS.map(p => ({
        pillar: p.short,
        score: selectedEntry.scores[p.key] || 0,
        fullMark: 10,
      }))
    : [];

  const trendData = [...history].reverse().slice(-30).map(entry => ({
    date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    score: entry.overall_score,
  }));

  if (loading) {
    return (
      <div className="bg-cream min-h-screen pt-24 flex items-center justify-center">
        <p className="text-stone-400 font-body">Loading...</p>
      </div>
    );
  }

  const isGuest = !user;

  // Lowest pillars for guidance
  const sortedPillars = allScored
    ? [...PILLARS].sort((a, b) => (scores[a.key] || 0) - (scores[b.key] || 0))
    : [];
  const lowestThree = sortedPillars.slice(0, 3);

  return (
    <div className="bg-cream min-h-screen pt-24 pb-16" data-testid="wellness-wheel-page">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <Link
          to={isGuest ? '/programs/new-client' : '/programs/terrain'}
          className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-6"
          data-testid="back-to-program"
        >
          <ArrowLeft size={18} />
          <span className="font-body text-sm">{isGuest ? 'Back to New Client Intake' : 'Back to SLOW WELLNESS'}</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-4xl sm:text-5xl text-forest mb-3" data-testid="wellness-wheel-title">
            Wellness Wheel
          </h1>
          <p className="text-stone-600 font-body text-base sm:text-lg leading-relaxed">
            Based on the 9 Pillars of Wellness — rate each area of your life today and receive personalized guidance.
          </p>
        </motion.div>

        {/* Prefer paper? Print options */}
        <div className="mb-8 bg-white rounded-2xl border border-stone-100 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="print-panel">
          <div className="flex-1">
            <p className="font-heading text-lg text-forest">Prefer paper?</p>
            <p className="text-stone-500 font-body text-sm">Print a reflection guide with all the prompts, or a page of blank wheels to fill in by hand.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={printGuide}
              className="inline-flex items-center gap-2 bg-forest text-cream font-body text-sm px-4 py-2.5 rounded-full hover:bg-forest-dark transition-colors"
              data-testid="print-guide-btn"
            >
              <Printer size={15} /> Reflection Guide
            </button>
            <button
              onClick={printBlankWheels}
              className="inline-flex items-center gap-2 bg-stone-100 text-forest font-body text-sm px-4 py-2.5 rounded-full hover:bg-stone-200 transition-colors"
              data-testid="print-wheels-btn"
            >
              <Printer size={15} /> Blank Wheels
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 flex-wrap" data-testid="wheel-tabs">
          {[
            { id: 'assess', label: 'Today\'s Assessment' },
            ...(isGuest ? [] : [
              { id: 'history', label: 'History' },
              { id: 'trends', label: 'Trends' },
            ]),
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setView(tab.id)}
              className={`px-4 py-2 rounded-full font-body text-sm transition-all ${
                view === tab.id || (view === 'results' && tab.id === 'assess')
                  ? 'bg-forest text-cream'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ASSESSMENT VIEW */}
        {(view === 'assess') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {/* Progress */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="font-body text-sm text-stone-500">
                  {Object.keys(scores).length} of {PILLARS.length} pillars rated
                </span>
                {Object.keys(scores).length > 0 && (
                  <button
                    onClick={handleReset}
                    className="text-stone-400 hover:text-stone-600 transition-colors flex items-center gap-1 text-sm font-body"
                    data-testid="reset-scores"
                  >
                    <RotateCcw size={14} />
                    Reset
                  </button>
                )}
              </div>
              <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-forest rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${(Object.keys(scores).length / PILLARS.length) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>

            {/* Pillar Cards */}
            <div className="space-y-4">
              {PILLARS.map((pillar, idx) => {
                const isActive = currentPillar === idx;
                const scored = scores[pillar.key] !== undefined;
                return (
                  <motion.div
                    key={pillar.key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`bg-white rounded-xl border transition-all ${
                      isActive ? 'border-forest/30 shadow-md' : scored ? 'border-stone-200' : 'border-stone-100'
                    }`}
                    data-testid={`pillar-card-${pillar.key}`}
                  >
                    <button
                      onClick={() => setCurrentPillar(idx)}
                      className="w-full p-4 flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: pillar.color }}
                        />
                        <div>
                          <p className="font-body text-forest font-medium text-sm">{pillar.name}</p>
                          <p className="font-body text-stone-400 text-xs">{pillar.category}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {scored && (
                          <span
                            className="font-heading text-lg font-bold"
                            style={{ color: pillar.color }}
                          >
                            {scores[pillar.key]}
                          </span>
                        )}
                        <ChevronRight
                          size={16}
                          className={`text-stone-300 transition-transform ${isActive ? 'rotate-90' : ''}`}
                        />
                      </div>
                    </button>

                    <AnimatePresence>
                      {isActive && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-5 pt-1">
                            <p className="font-body text-stone-600 text-sm mb-4 italic">
                              "{pillar.question}"
                            </p>
                            {pillar.prompts && (
                              <div className="mb-5 bg-stone-50 rounded-lg p-4 border border-stone-100" data-testid={`prompts-${pillar.key}`}>
                                <p className="font-body text-forest text-xs font-semibold uppercase tracking-wide mb-2.5">
                                  Ask yourself
                                </p>
                                <ul className="space-y-2">
                                  {pillar.prompts.map((prompt, pi) => (
                                    <li key={pi} className="text-stone-600 font-body text-sm leading-relaxed pl-4 relative">
                                      <span className="absolute left-0 top-1.5 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: pillar.color }} />
                                      {prompt}
                                    </li>
                                  ))}
                                </ul>
                                <p className="text-stone-400 font-body text-xs italic mt-3">
                                  Let your honest answers guide the number you choose below.
                                </p>
                              </div>
                            )}
                            <div className="flex gap-1.5 flex-wrap">
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(val => (
                                <button
                                  key={val}
                                  onClick={() => {
                                    handleScore(pillar.key, val);
                                    if (idx < PILLARS.length - 1) {
                                      setTimeout(() => setCurrentPillar(idx + 1), 300);
                                    }
                                  }}
                                  className={`w-10 h-10 rounded-lg font-body text-sm font-medium transition-all ${
                                    scores[pillar.key] === val
                                      ? 'text-white shadow-md scale-110'
                                      : 'bg-stone-50 text-stone-600 hover:bg-stone-100'
                                  }`}
                                  style={
                                    scores[pillar.key] === val
                                      ? { backgroundColor: pillar.color }
                                      : {}
                                  }
                                  data-testid={`score-${pillar.key}-${val}`}
                                >
                                  {val}
                                </button>
                              ))}
                            </div>
                            <div className="flex justify-between mt-2">
                              <span className="text-xs text-stone-400 font-body">Needs deep support</span>
                              <span className="text-xs text-stone-400 font-body">Thriving</span>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>

            {/* Live Wheel Preview */}
            {Object.keys(scores).length >= 3 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 bg-white rounded-2xl p-6 border border-stone-100"
              >
                <h3 className="font-heading text-xl text-forest mb-4 text-center">Your Wellness Wheel</h3>
                <div className="h-72 sm:h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                      <PolarGrid stroke="#E6E2DA" />
                      <PolarAngleAxis
                        dataKey="pillar"
                        tick={{ fill: '#6B706B', fontSize: 11, fontFamily: 'Manrope' }}
                      />
                      <PolarRadiusAxis
                        domain={[0, 10]}
                        tick={{ fill: '#9CA099', fontSize: 10 }}
                        axisLine={false}
                      />
                      <Radar
                        name="Today"
                        dataKey="score"
                        stroke="#2C3E2E"
                        fill="#2C3E2E"
                        fillOpacity={0.15}
                        strokeWidth={2}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            )}

            {/* Save Button */}
            {allScored && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6"
              >
                <div className="text-center mb-4">
                  <p className="font-heading text-2xl text-forest">
                    Overall Score: <span className="text-terracotta">{overallScore}</span> / 10
                  </p>
                </div>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full bg-forest text-cream py-4 rounded-xl font-body font-medium hover:bg-forest-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="save-assessment"
                >
                  <Save size={18} />
                  {saving ? 'Saving...' : (isGuest ? 'View Your Wheel' : 'Save & View Guidance')}
                </button>
                {isGuest && (
                  <p className="text-xs text-stone-400 font-body text-center mt-2">
                    Sign in with a SLOW WELLNESS account to save your results and track progress over time.
                  </p>
                )}
              </motion.div>
            )}
          </motion.div>
        )}

        {/* RESULTS VIEW */}
        {view === 'results' && allScored && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {/* Wheel */}
            <div className="bg-white rounded-2xl p-6 border border-stone-100 mb-6">
              <h3 className="font-heading text-xl text-forest mb-2 text-center">
                Today's Wellness Wheel
              </h3>
              <p className="text-center text-stone-500 font-body text-sm mb-4">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
              <div className="h-72 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                    <PolarGrid stroke="#E6E2DA" />
                    <PolarAngleAxis
                      dataKey="pillar"
                      tick={{ fill: '#6B706B', fontSize: 11, fontFamily: 'Manrope' }}
                    />
                    <PolarRadiusAxis
                      domain={[0, 10]}
                      tick={{ fill: '#9CA099', fontSize: 10 }}
                      axisLine={false}
                    />
                    <Radar
                      name="Today"
                      dataKey="score"
                      stroke="#2C3E2E"
                      fill="#2C3E2E"
                      fillOpacity={0.15}
                      strokeWidth={2}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Overall Score */}
            {(() => {
              const msg = getOverallMessage(parseFloat(overallScore));
              return (
                <div
                  className="rounded-2xl p-6 mb-6 border"
                  style={{ backgroundColor: msg.color + '10', borderColor: msg.color + '30' }}
                >
                  <h3 className="font-heading text-xl mb-2" style={{ color: msg.color }}>{msg.title}</h3>
                  <p className="text-stone-600 font-body text-sm leading-relaxed">{msg.message}</p>
                  <p className="font-heading text-3xl mt-3" style={{ color: msg.color }}>
                    {overallScore} <span className="text-lg text-stone-400">/ 10</span>
                  </p>
                </div>
              );
            })()}

            {/* Pillar Scores Summary */}
            <div className="bg-white rounded-2xl p-6 border border-stone-100 mb-6">
              <h3 className="font-heading text-lg text-forest mb-4">Pillar Breakdown</h3>
              <div className="space-y-3">
                {[...PILLARS].sort((a, b) => (scores[a.key] || 0) - (scores[b.key] || 0)).map(pillar => (
                  <div key={pillar.key} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: pillar.color }} />
                    <span className="font-body text-sm text-stone-600 w-36 flex-shrink-0">{pillar.short}</span>
                    <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(scores[pillar.key] / 10) * 100}%` }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="h-full rounded-full"
                        style={{ backgroundColor: pillar.color }}
                      />
                    </div>
                    <span className="font-heading text-sm font-bold w-6 text-right" style={{ color: pillar.color }}>
                      {scores[pillar.key]}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Guidance Section */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="font-heading text-lg text-forest">Today's Guidance</h3>
                <button onClick={() => setShowInfo(!showInfo)} className="text-stone-400 hover:text-stone-600">
                  <Info size={16} />
                </button>
              </div>
              {showInfo && (
                <p className="text-stone-500 font-body text-xs mb-4 bg-stone-50 rounded-lg p-3">
                  Guidance is based on your lowest-scoring pillars. Focus on just 1-2 areas — when the most strained areas are supported, the entire system begins to regulate.
                </p>
              )}
              <div className="space-y-4">
                {lowestThree.map(pillar => {
                  const level = getScoreLevel(scores[pillar.key]);
                  const tips = pillar.guidance[level];
                  return (
                    <div
                      key={pillar.key}
                      className="bg-white rounded-xl p-5 border-l-4"
                      style={{ borderLeftColor: pillar.color }}
                      data-testid={`guidance-${pillar.key}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: pillar.color }} />
                        <h4 className="font-body text-forest font-medium text-sm">{pillar.name}</h4>
                        <span className="ml-auto font-heading text-sm" style={{ color: pillar.color }}>
                          {scores[pillar.key]}/10
                        </span>
                      </div>
                      <ul className="space-y-2">
                        {tips.map((tip, i) => (
                          <li key={i} className="text-stone-600 font-body text-sm leading-relaxed pl-4 relative">
                            <span className="absolute left-0 top-1.5 w-1.5 h-1.5 rounded-full bg-stone-300" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleReset}
                className="flex-1 bg-stone-100 text-forest py-3 rounded-xl font-body text-sm hover:bg-stone-200 transition-colors flex items-center justify-center gap-2"
                data-testid="retake-assessment"
              >
                <RotateCcw size={16} />
                Retake Today
              </button>
              <button
                onClick={() => setView('history')}
                className="flex-1 bg-forest text-cream py-3 rounded-xl font-body text-sm hover:bg-forest-dark transition-colors flex items-center justify-center gap-2"
                data-testid="view-history"
              >
                <History size={16} />
                View History
              </button>
            </div>
          </motion.div>
        )}

        {/* HISTORY VIEW */}
        {view === 'history' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {loadingHistory ? (
              <div className="text-center py-12">
                <p className="text-stone-500 font-body">Loading history...</p>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-stone-500 font-body mb-4">No assessments yet. Take your first one today!</p>
                <button
                  onClick={() => setView('assess')}
                  className="bg-forest text-cream px-6 py-3 rounded-full font-body hover:bg-forest-dark transition-colors"
                >
                  Start Assessment
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map(entry => (
                  <motion.div
                    key={entry.date}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md ${
                      selectedEntry?.date === entry.date ? 'border-forest/30 shadow-md' : 'border-stone-100'
                    }`}
                    onClick={() => setSelectedEntry(selectedEntry?.date === entry.date ? null : entry)}
                    data-testid={`history-entry-${entry.date}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-body text-forest font-medium text-sm">
                          {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </p>
                        <p className="font-body text-stone-400 text-xs mt-0.5">
                          {entry.date === todayStr ? "Today's entry" : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-heading text-xl text-terracotta font-bold">
                          {entry.overall_score}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this entry?')) handleDelete(entry.date);
                          }}
                          className="text-stone-300 hover:text-red-400 transition-colors p-1"
                          data-testid={`delete-entry-${entry.date}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Expanded entry details */}
                    <AnimatePresence>
                      {selectedEntry?.date === entry.date && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-4 pt-4 border-t border-stone-100">
                            <div className="h-64">
                              <ResponsiveContainer width="100%" height="100%">
                                <RadarChart data={selectedRadarData} cx="50%" cy="50%" outerRadius="65%">
                                  <PolarGrid stroke="#E6E2DA" />
                                  <PolarAngleAxis
                                    dataKey="pillar"
                                    tick={{ fill: '#6B706B', fontSize: 10, fontFamily: 'Manrope' }}
                                  />
                                  <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
                                  <Radar
                                    dataKey="score"
                                    stroke="#C88D74"
                                    fill="#C88D74"
                                    fillOpacity={0.2}
                                    strokeWidth={2}
                                  />
                                </RadarChart>
                              </ResponsiveContainer>
                            </div>
                            <div className="grid grid-cols-3 gap-2 mt-2">
                              {PILLARS.map(p => (
                                <div key={p.key} className="flex items-center gap-1.5">
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                                  <span className="font-body text-xs text-stone-500">{p.short}</span>
                                  <span className="font-body text-xs font-medium ml-auto" style={{ color: p.color }}>
                                    {entry.scores[p.key]}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* TRENDS VIEW */}
        {view === 'trends' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {history.length < 2 ? (
              <div className="text-center py-12">
                <p className="text-stone-500 font-body mb-4">
                  Complete at least 2 assessments to see your trends.
                </p>
                <button
                  onClick={() => setView('assess')}
                  className="bg-forest text-cream px-6 py-3 rounded-full font-body hover:bg-forest-dark transition-colors"
                >
                  Start Assessment
                </button>
              </div>
            ) : (
              <>
                {/* Overall Score Trend */}
                <div className="bg-white rounded-2xl p-6 border border-stone-100 mb-6">
                  <h3 className="font-heading text-lg text-forest mb-4">Overall Wellness Trend</h3>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E2DA" />
                        <XAxis dataKey="date" tick={{ fill: '#6B706B', fontSize: 11, fontFamily: 'Manrope' }} />
                        <YAxis domain={[0, 10]} tick={{ fill: '#9CA099', fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#FDFBF7',
                            border: '1px solid #E6E2DA',
                            borderRadius: '8px',
                            fontFamily: 'Manrope',
                            fontSize: 12,
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="score"
                          stroke="#2C3E2E"
                          strokeWidth={2}
                          dot={{ fill: '#C88D74', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6, fill: '#C88D74' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Per-Pillar Trends */}
                <div className="bg-white rounded-2xl p-6 border border-stone-100">
                  <h3 className="font-heading text-lg text-forest mb-4">Pillar Trends</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={[...history].reverse().slice(-30).map(entry => ({
                        date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                        ...PILLARS.reduce((acc, p) => ({ ...acc, [p.short]: entry.scores[p.key] || 0 }), {}),
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E2DA" />
                        <XAxis dataKey="date" tick={{ fill: '#6B706B', fontSize: 10, fontFamily: 'Manrope' }} />
                        <YAxis domain={[0, 10]} tick={{ fill: '#9CA099', fontSize: 10 }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#FDFBF7',
                            border: '1px solid #E6E2DA',
                            borderRadius: '8px',
                            fontFamily: 'Manrope',
                            fontSize: 11,
                          }}
                        />
                        {PILLARS.map(p => (
                          <Line
                            key={p.key}
                            type="monotone"
                            dataKey={p.short}
                            stroke={p.color}
                            strokeWidth={1.5}
                            dot={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-4 justify-center">
                    {PILLARS.map(p => (
                      <div key={p.key} className="flex items-center gap-1.5">
                        <div className="w-3 h-0.5 rounded" style={{ backgroundColor: p.color }} />
                        <span className="font-body text-xs text-stone-500">{p.short}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default WellnessWheelPage;
